import {
  DashboardObjectPositionAndSizeUpdate,
  DataType,
  DataValue,
  DomainRestrictionType,
  ParameterInfo,
} from '@tableau/api-internal-contract-js';
import { ParameterCtrlPresModel, ZonePositionAndSizeUpdatePresModel } from '../../gen-src/preslayer/PresModels';
import { ErrorHelpers } from '../ErrorHelpers';
import { PresLayerToApiEnumMappings as EnumMapping, ShouldThrow } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';

/**
 * Contains logic for converting DashboardObjectPositionAndSizeUpdates into their equivalent PresModels
 */
export class DashboardObjectPositionAndSizeUpdateConverter {
  public static convertToZonePositionAndSizeUpdatePresModelArray(
    dashboardObjectPositionAndSizeUpdateArray: Array<DashboardObjectPositionAndSizeUpdate>,
  ): Array<ZonePositionAndSizeUpdatePresModel> {
    return dashboardObjectPositionAndSizeUpdateArray.map(
      DashboardObjectPositionAndSizeUpdateConverter.convertToZonePositionAndSizeUpdatePresModel,
    );
  }

  // Converts a single parameter.
  public static convertToZonePositionAndSizeUpdatePresModel(
    dashboardObjectPositionAndSizeUpdate: DashboardObjectPositionAndSizeUpdate,
  ): ZonePositionAndSizeUpdatePresModel {
    const result: ZonePositionAndSizeUpdatePresModel = {
      zoneId: dashboardObjectPositionAndSizeUpdate.dashboardObjectID,
      x: dashboardObjectPositionAndSizeUpdate.x,
      y: dashboardObjectPositionAndSizeUpdate.y,
      w: dashboardObjectPositionAndSizeUpdate.width,
      h: dashboardObjectPositionAndSizeUpdate.height,
    };

    return result;
  }
}

/**
 * Contains logic for converting Parameter models into their equivalent Api versions
 */
export class ParametersDataConverter {
  public static convertParameterList(parameters: Array<ParameterCtrlPresModel>): Array<ParameterInfo> {
    return parameters.map(ParametersDataConverter.convertParameter);
  }

  // Converts a single parameter.
  // Rough port of https://opengrok/source/xref/dev_nessie/workgroup/vqlweb/scriptsharp/src/UI/Api/ApiParameterHandler.cs#95
  public static convertParameter(param: ParameterCtrlPresModel): ParameterInfo {
    const currentValue = ParametersDataConverter.convertAliasedDataValue(
      PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(param.valueAlias),
      param.formattedValue,
    );

    const allowableValuesType = EnumMapping.parameterTypesDomainType.convert(param.parameterDomainType);

    const result: ParameterInfo = {
      name: param.fieldCaption,
      fieldName: param.parameterName,
      dataType: EnumMapping.dataType.convert(param.dataType),
      currentValue: currentValue,
      allowableValuesType: allowableValuesType,
    };

    if (allowableValuesType === DomainRestrictionType.List) {
      const allowableValues = new Array<DataValue>();
      for (let i = 0; i < param.valuesAliases.length; i++) {
        allowableValues.push(ParametersDataConverter.convertAliasedDataValue(param.valuesAliases[i], param.formattedValues[i]));
      }

      result.allowableValues = allowableValues;
    } else if (allowableValuesType === DomainRestrictionType.Range) {
      result.minValue = ParametersDataConverter.makeDataValue(
        PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(param.valueMin),
        param.formattedMin,
      );
      result.maxValue = ParametersDataConverter.makeDataValue(
        PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(param.valueMax),
        param.formattedMax,
      );
      result.stepSize = ParametersDataConverter.convertNumber(
        result.dataType,
        PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(param.valueInc),
      );
      if (result.dataType === DataType.Date || result.dataType === DataType.DateTime) {
        result.dateStepPeriod = EnumMapping.dateStepPeriod.convert(param.datePeriodType, ShouldThrow.No);
      }
    } else {
      // Nothing to do in the case that all values are allowed
    }

    return result;
  }

  private static convertNumber(dataType: DataType, value: string): number | undefined {
    if (value) {
      if (dataType === DataType.Int || dataType === DataType.Date || dataType === DataType.DateTime) {
        return parseInt(value, 10);
      } else if (dataType === DataType.Float) {
        return parseFloat(value);
      }
    }

    return undefined;
  }

  private static makeDataValue(value: string, formattedValue: string, alias?: string): DataValue {
    const result: DataValue = {
      value: value,
      formattedValue: formattedValue,
      aliasedValue: alias || formattedValue,
      hasAlias: !!alias,
    };

    return result;
  }

  private static convertAliasedDataValue(
    /*tslint:disable-next-line:no-any */
    valueAlias: Array<string>,
    formattedValue: string,
  ): DataValue {
    // AliasedDataValues are serialized in kind of a strange way from the platform code. They are output
    // as an array of 2 values. The first is the actual value as string, the second is the alias.
    // See AliasedDataValueSerializer::ToSerializer
    if (!valueAlias || !(valueAlias instanceof Array)) {
      throw ErrorHelpers.presModelParsingError('ParameterCtrlPresModel');
    }

    const alias = valueAlias.length < 2 ? undefined : valueAlias[1];
    const nativeValueString = valueAlias[0];
    return ParametersDataConverter.makeDataValue(nativeValueString, formattedValue, alias);
  }
}
